import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Filter } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; };
const getMonthRange = () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-01`, end: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(new Date(n.getFullYear(), n.getMonth()+1, 0).getDate()).padStart(2,'0')}` }; };

const PurchaseReport = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, rows: [] });
  const [suppliers, setSuppliers] = useState([]);
  const mr = getMonthRange();
  const [startDate, setStartDate] = useState(mr.start);
  const [endDate, setEndDate] = useState(mr.end);
  const [supplierId, setSupplierId] = useState('');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    api.get('/suppliers').then(r => setSuppliers(r.data || [])).catch(() => {});
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (supplierId) params.set('supplier_id', supplierId);
      if (status && status !== 'all') params.set('status', status);
      const r = await api.get(`/reports/purchase?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (supplierId) params.set('supplier_id', supplierId);
    if (status && status !== 'all') params.set('status', status);
    window.open(`${BACKEND_URL}/api/reports/purchase/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (supplierId) params.set('supplier_id', supplierId);
    if (status && status !== 'all') params.set('status', status);
    window.open(`${BACKEND_URL}/api/reports/purchase/export-csv?${params}`, '_blank');
  };

  const s = data.summary || {};
  const statusColors = { draft: 'bg-[#F5F5F5] text-[#6b7280]', sent: 'bg-[#DBEAFE] text-[#1E40AF]', received: 'bg-[#DBEAFE] text-[#1E40AF]', partially_received: 'bg-[#FEF3C7] text-[#92400E]', cancelled: 'bg-[#FEE2E2] text-[#991B1B]' };
  const totalPurchased = (data.rows || []).reduce((a, r) => a + r.total_amount, 0);
  const totalPaid = (data.rows || []).reduce((a, r) => a + r.paid_amount, 0);
  const totalBalance = (data.rows || []).reduce((a, r) => a + r.balance, 0);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="purchase-report-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="purchase-report-title">Purchase Report</h1>
            <div className="flex gap-2">
              <button onClick={exportPdf} className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-sm text-sm" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />Export PDF</button>
              <button onClick={exportCsv} className="flex items-center gap-2 border border-[#e5e7eb] bg-white text-[#1a1a2e] px-4 py-2 rounded-sm text-sm hover:bg-[#F5F5F5] transition-colors" data-testid="export-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="purchase-report-filters">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="start-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="end-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Supplier</label>
              <select value={supplierId} onChange={e => setSupplierId(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="supplier-filter">
                <option value="">All Suppliers</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="status-filter">
                <option value="all">All</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="partially_received">Partially Received</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={fetchReport} className="w-full flex items-center justify-center gap-2 bg-[#1a1a2e] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#27272A] transition-colors" data-testid="apply-filters-btn"><Filter className="w-4 h-4" />Apply</button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-purchased-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Total Purchased</p>
              <p className="text-xl font-semibold text-[#1a1a2e]">{fmtRs(s.total_purchased)}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-paid-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Total Paid</p>
              <p className="text-xl font-semibold text-[#1E40AF]">{fmtRs(s.total_paid)}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-outstanding-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Total Outstanding</p>
              <p className="text-xl font-semibold text-[#EF4444]">{fmtRs(s.total_outstanding)}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="po-count-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Number of POs</p>
              <p className="text-xl font-semibold text-[#1a1a2e]">{s.po_count || 0}</p>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-[#6b7280]">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left min-w-[600px]" data-testid="purchase-report-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4">PO #</th>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Supplier</th>
                      <th className="py-3 px-4 text-right">Total Amount</th>
                      <th className="py-3 px-4 text-right">Paid Amount</th>
                      <th className="py-3 px-4 text-right">Balance</th>
                      <th className="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map((row, i) => (
                      <tr key={i} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                        <td className="py-3 px-4 font-medium text-[#1a1a2e]">{row.po_number}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{fmtDate(row.date)}</td>
                        <td className="py-3 px-4 text-[#1a1a2e]">{row.supplier_name}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(row.total_amount)}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(row.paid_amount)}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(row.balance)}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[row.status] || 'bg-[#F5F5F5] text-[#6b7280]'}`}>{row.status}</span></td>
                      </tr>
                    ))}
                    {(data.rows || []).length > 0 && (
                      <tr className="bg-[#F5F5F5] font-bold border-t-2 border-[#e5e7eb]" data-testid="purchase-totals-row">
                        <td className="py-3 px-4 text-[#1a1a2e]">TOTAL</td>
                        <td className="py-3 px-4"></td>
                        <td className="py-3 px-4"></td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(totalPurchased)}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(totalPaid)}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(totalBalance)}</td>
                        <td className="py-3 px-4"></td>
                      </tr>
                    )}
                    {(data.rows || []).length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-[#9ca3af]">No purchase orders found for the selected filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PurchaseReport;
